import { getCookie } from '../../helpers/cookie/get';
import { setCookie } from '../../helpers/cookie/set';
import { hasCookie } from '../../helpers/cookie/has';
import { addDays } from '../../helpers/datetime/add-days';
import { addAnimationEndListener } from '../../helpers/animation/add-animation-end-listener';

export interface CookieBannerOptions {
	boxSelector: string;
	runAfterLoad?: (banner: CookieBanner) => void;
	cookieAttributeName: string;
	cookieName: string;
	acceptCookieButton: string;
	declineCookieButton: string;
	animationClearBgOnDcline: string;
	animationClearBgOnAccept: string;
	animationDecline: string;
	animationAccept: string;
	showClass: string;
}

export function loadGoogleTagManager(code: string | null) {
	if (!code) {
		return;
	}
	(function (w, d, s, l, i) {
		w[l] = w[l] || [];
		w[l].push({
			'gtm.start': new Date().getTime(),
			event: 'gtm.js',
		});
		const f = d.getElementsByTagName(s)[0],
			j = d.createElement(s) as HTMLScriptElement,
			dl = l !== 'dataLayer' ? '&l=' + l : '';
		j.async = true;
		j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
		f?.parentNode?.insertBefore(j, f);
	})(window, document, 'script', 'dataLayer', code);
}

export class CookieBanner {
	static defaults: CookieBannerOptions = {
		boxSelector: '.js-cookie-banner-box',
		runAfterLoad: (banner: CookieBanner) => {
			loadGoogleTagManager(banner.banner.getAttribute('data-cookie-gtm'));
		},
		cookieAttributeName: 'data-cookie-name',
		cookieName: 'Opten.OPTEN_COOKIE.CONSENT',
		acceptCookieButton: '.js-accept-button',
		declineCookieButton: '.js-close-button',
		animationClearBgOnDcline: 'is-declined',
		animationClearBgOnAccept: 'is-accepted',
		animationDecline: 'is-declined',
		animationAccept: 'is-accepted',
		showClass: 'show',
	};

	private approveButton: HTMLElement | null;
	private declineButton: HTMLElement | null;
	private dataBox: HTMLElement | null;
	private cookieName: string;
	private options: CookieBannerOptions;

	constructor(
		public banner: HTMLElement,
		options?: Partial<CookieBannerOptions>
	) {
		this.options = { ...CookieBanner.defaults, ...options };
		this.cookieName =
			banner.getAttribute(this.options.cookieAttributeName) ||
			this.options.cookieName;
		this.dataBox = banner.querySelector(this.options.boxSelector);
		this.approveButton = banner.querySelector(this.options.acceptCookieButton);
		this.declineButton = banner.querySelector(this.options.declineCookieButton);
		if (this.checkCookie(this.cookieName)) {
			this.closeBannerAccept();
		}
		this.declineButton?.addEventListener('click', () =>
			this.onButtonCklickDecline()
		);
		this.approveButton?.addEventListener('click', () =>
			this.onButtonClickAccept()
		);
	}

	private closeBannerDecline() {
		addAnimationEndListener(this.banner, () => {
			this.banner.classList.remove(this.options.animationClearBgOnDcline);
		});
		if (this.dataBox) {
			addAnimationEndListener(this.dataBox, () => {
				this.dataBox?.classList.remove(this.options.animationDecline);
				this.banner.classList.remove(this.options.showClass);
			});
		}
		this.banner.classList.add(this.options.animationClearBgOnDcline);
		this.dataBox?.classList.add(this.options.animationDecline);
	}

	private closeBannerAccept() {
		addAnimationEndListener(this.banner, () => {
			this.banner.classList.remove(this.options.animationClearBgOnAccept);
		});
		if (this.dataBox) {
			addAnimationEndListener(this.dataBox, () => {
				this.dataBox?.classList.remove(this.options.animationAccept);
				this.banner.classList.remove(this.options.showClass);
			});
		}
		this.banner.classList.add(this.options.animationClearBgOnAccept);
		this.dataBox?.classList.add(this.options.animationAccept);
	}

	private checkCookie(name: string) {
		if (hasCookie(name)) {
			const value = getCookie(this.cookieName);
			return (value && value === 'true') || value === 'false';
		}
		return false;
	}

	private onButtonCklickDecline() {
		setCookie(this.cookieName, 'false', addDays(new Date(Date.now()), 15));
		this.closeBannerDecline();
	}

	private onButtonClickAccept() {
		setCookie(this.cookieName, 'true', addDays(new Date(Date.now()), 365));
		this.closeBannerAccept();
		if (this.options.runAfterLoad) {
			this.options.runAfterLoad(this);
		}
	}
}
